import { Tableau } from '../../common/utilities/TableauLog';
import { Module } from '../Module';
import { TagRequest } from '../ServerApi/requests/TagRequest';
import { IRequest } from '../ServerApi/types/IRequest';
import { IResult } from '../ServerApi/types/IResponse';
import { IServer } from '../ServerApi/types/IServer';
import { ErrorReason, ServerService } from './ServerService';
import { ToasterService, ToastType } from './ToasterService';

export class ErrorNotifyingServer implements IServer {

  public static ResponseDelay = 2000;

  public static $inject = ['ServerService', 'ToasterService', '$translate', '$timeout'];

  constructor(
    private ServerService: ServerService,
    private toaster: ToasterService,
    private $translate: ng.translate.ITranslateService,
    private $timeout: ng.ITimeoutService
  ){}

  private notification(reason: any, showToast: boolean){
    let errorMessage = this.$translate.instant('unexpectedError');
    if (reason.errors && reason.errors.length > 0 && reason.errors[0].message){
      errorMessage = errorMessage.concat('\n ' + reason.errors[0].message);
    }
    Tableau.Logger().error('Unknown error from server: ' + errorMessage);
    if (showToast) {
      this.toaster.pop({ type: ToastType.Error, text: errorMessage});
    }
  }

  // this will throw a toast if the http call itself fails, not if the server returns a
  // valid http response that says it had an internal error
  public notifyOnFail<TResult>(result: ng.IPromise<TResult>): ng.IPromise<TResult> {
    result.catch((reason: ErrorReason) => {
      if (reason === ErrorReason.Unknown) {
        this.notification(reason, true);
      }
    });
    result.then((data: any) => {
      if (data.errors && data.errors.length > 0) {
        this.notification(data, false);
      }
      return data;
    });
    return result;
  }

  public addDelay<T>(result: ng.IPromise<T>): ng.IPromise<T> {
    return result.then((value: T) => this.$timeout(() => value, ErrorNotifyingServer.ResponseDelay));
  }

  public sendRequest<TParams, TResult extends IResult>(
    request: IRequest<TParams, TResult>
  ): ng.IPromise<TResult> {
    return this.notifyOnFail(this.ServerService.sendRequest(request));
  }

  public sendTagRequest<TParams, TResult extends IResult>(
    request: TagRequest<TParams, TResult>
  ): ng.IPromise<TResult> {
    return this.notifyOnFail(this.ServerService.sendTagRequest(request));
  }

  public sendRequestAndDelay<TParams, TResult extends IResult>(
    request: IRequest<TParams, TResult>
  ): ng.IPromise<TResult> {
    return this.addDelay(this.sendRequest(request));
  }

  public sendRequests<TItem, TParams, TResult>(
    requests: IRequest<TParams, TResult>[],
    combineResults: (results: TResult[]) => TResult
  ): ng.IPromise<TResult> {
    return this.notifyOnFail(this.ServerService.sendRequests(requests, combineResults));
  }

  public sendRequestsAndDelay<TItem, TParams, TResult>(
    requests: IRequest<TParams, TResult>[],
    combineResults: (results: TResult[]) => TResult
  ): ng.IPromise<TResult> {
    return this.addDelay(this.sendRequests(requests, combineResults));
  }

  public getServerService(): ServerService {
    return this.ServerService;
  }

}

Module.instance().service('ErrorNotifyingServer', ErrorNotifyingServer);
